import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FeaturesModule } from './features/features.module';
import { SharedModule } from './shared/shared.module';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { StoreModule, Store } from '@ngrx/store';
import { StoreRouterConnectingModule, RouterStateSerializer } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';

import { environment } from '../environments/environment';

import { reducers, metaReducers, AppState } from './reducers';
import { CustomRouterStateSerializer } from './shared/utils';
import { AuthenticatedGuard } from './global/authenticated.guard';
import { HIGHCHARTS_MODULES } from './shared/charts/charts.service';

import * as more from 'highcharts/highcharts-more';
import * as exporting from 'highcharts/modules/exporting';
import * as drilldown from 'highcharts/modules/drilldown';
import * as treemap from 'highcharts/modules/treemap';
import * as stock from 'highcharts/modules/stock';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthenticationInterceptor } from './global/authentication.interceptor';
import { HttpService } from './global/http.service';
import { take } from 'rxjs/operators';
import { setState } from './global/session.service';
import { TestAutomationInterceptor } from './global/test-automation.interceptor';
import { AuthenticationModule } from 'ngpd-merceros-authentication-fe-components';
import { LoginGuard } from './global/login.guard';
import { provideRoutes, Route } from '@angular/router';
import { MainComponent } from './shared/layout/main.component';
import { WindowResizeService } from './global/window-resize.service';
import { IconModule } from 'atlas-icon';
import { CdkModule } from 'atlas-cdk';
import { AlertModule } from 'atlas-alert';
import { ToastService } from './shared/toaster_/toaster_.service';
import { Overlay, RepositionScrollStrategy } from '@angular/cdk/overlay';
import { DATEPICKER_SCROLL_STRATEGY } from 'atlas-field';
import { NavigationMenuModule } from 'atlas-navigation';
import { MenuModule, DropdownTriggerType } from 'atlas-menu';
import { ListModule } from 'atlas-list';
import { AvatarModule } from 'atlas-avatar';
import { LinkModule } from 'atlas-link';
/**
 * Lazy loaded routes described in this function are conditionally included or excluded on-build.
 * Excluded routes, store, and components will not be registered, code will not be present in build artifacts.
 */
export function loadModules(): Route[] {
  return !environment.internal ? [] : [
    {
      path: '',
      component: MainComponent,
      canActivate: [LoginGuard, AuthenticatedGuard],
      children: [
        {
          path: 'administration',
          // loadChildren: './features/administration/administration.module#AdministrationModule'
          loadChildren: () => import('./features/administration/administration.module').then(m => m.AdministrationModule)
        }
      ]
    }
  ];
}

@NgModule({
  imports: [
    BrowserAnimationsModule,
    CommonModule,
    AppRoutingModule,
    FeaturesModule,
    SharedModule,
    IconModule,
    CdkModule,
    AlertModule,
    NavigationMenuModule,
    LinkModule,
    MenuModule,
    AvatarModule,
    ListModule,
    /**
     * StoreModule.forRoot is imported once in the root module, accepting a reducer
     * function or object map of reducer functions. If passed an object of
     * reducers, combineReducers will be run creating your application
     * meta-reducer. This returns all providers for an @ngrx/store
     * based application.
     */
    StoreModule.forRoot(reducers, { metaReducers }),

    /**
     * @ngrx/router-store keeps router state up-to-date in the store.
     */
    StoreRouterConnectingModule.forRoot(),

    /**
     * Store devtools instrument the store retaining past versions of state
     * and recalculating new states. This enables powerful time-travel
     * debugging.
     *
     * To use the debugger, install the Redux Devtools extension for either
     * Chrome or Firefox
     *
     * See: https://github.com/zalmoxisus/redux-devtools-extension
     */
    environment.production ? [] : StoreDevtoolsModule.instrument({
      name: 'NgRx Store DevTools',
      logOnly: false,
    }),

    /**
     * EffectsModule.forRoot() is imported once in the root module and
     * sets up the effects class to be initialized immediately when the
     * application starts.
     *
     * See: https://github.com/ngrx/platform/blob/master/docs/effects/api.md#forroot
     */
    EffectsModule.forRoot([]),

    /**
     * MSSO and MFA configuration
     */
    AuthenticationModule.forRoot({
      common: {
        appName: environment.appName,
        apiUrl: environment.API,
        customNotifications: true,
        loginSuccessRoute: '/overview',
        logoutRoute: '/provision/login',
        jwtTokenExpiresIn: +environment.jwtTokenExpiresIn || 285, // 5 minutes (-15 seconds jwt skew)
        refreshTokenExpiresIn: +environment.refreshTokenExpiresIn || 1200, // 20 minutes
        disableSessionTimeout: true,
        // devMode: true,
        devMode: false,
      },
      msso: {
        bypassMsso: false,
        federatedLoginUrl: environment.API + '/msso/login/federated',
        homeRealmUrl: environment.API + '/msso/homerealm',
        mssoFailureUrl: environment.API + '/msso/login/failed',
        mssoLoginUrl: environment.mssoLoginPost,
        mssoSupportEmail: environment.mfaSupportEmail,
        rpid: environment.rpid,
      },
      mfa: {
        bypassMfa: !environment.mfaEnabled,
        // bypassMfa: true,
        mfaSupportEmail: environment.mfaSupportEmail,
        enableMfaForFederatedUsers: true,
        defaultLockoutFlow: true
      },
    })
  ],
  providers: [
    /**
     * The `RouterStateSnapshot` provided by the `Router` is a large complex structure.
     * A custom RouterStateSerializer is used to parse the `RouterStateSnapshot` provided
     * by `@ngrx/router-store` to include only the desired pieces of the snapshot.
     */

    { provide: HttpService, useClass: HttpService },
    { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
    { provide: HIGHCHARTS_MODULES, useFactory: () => [more, exporting, drilldown, treemap, stock] },
    { provide: HTTP_INTERCEPTORS, useClass: AuthenticationInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TestAutomationInterceptor, multi: true },
    WindowResizeService,
    AuthenticatedGuard,
    LoginGuard,
    // MosSVGInlinerService,
    ToastService,
    provideRoutes(loadModules()),
    {
      provide: DATEPICKER_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: (overlay: Overlay) =>
        (): RepositionScrollStrategy =>
          overlay.scrollStrategies.reposition({ scrollThrottle: 0 }),
    },
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent]
})
export class AppModule {
  public constructor(
    private readonly store: Store<AppState>,
    // private readonly svg: MosSVGInlinerService
  ) {
    /**
     * Loads all SVG icons to render them consistently.
     */
    // svg.preloadAll();

    /**
     * Listens for page refresh event and stores state snapshot to session storage
     * for use in metaReducer `stateSetter`.
     */
    window.onbeforeunload = () => {
      store.pipe(take(1)).subscribe(state => {
        if (state.auth.loggedIn) {
          setState(state);
        }
      });
    };
  }
}
